import { useState, useCallback, useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { useWordStore } from '../stores/wordStore';
import { useUiStore } from '../stores/uiStore';
import { usePipelineStore } from '../stores/pipelineStore';
import { useStaleStore } from '../stores/staleStore';
import { useQuickApproveStore } from '../stores/quickApproveStore';
import { useConfigStore } from '../stores/configStore';
import { api, ApiError } from '../services/api';
import { STAGE_CONFIG, STAGE_TASK_MAP } from '../utils/stageConfig';
import useLearningCapture from '../hooks/useLearningCapture';
import StageNav from '../components/StageNav';
import WordEntryForm from '../components/stages/WordEntryForm';
import PickStage, { type Stage4EvidenceRecord } from '../components/stages/PickStage';
import ReviewStage from '../components/stages/ReviewStage';
import ReframesStage from '../components/stages/ReframesStage';
import AudioStage from '../components/stages/AudioStage';
import RelatedStage from '../components/stages/RelatedStage';
import FinalReviewStage from '../components/stages/FinalReviewStage';
import TransferStage from '../components/stages/TransferStage';
import RateLimitCountdown, { type RateLimitCountdownHandle } from '../components/RateLimitCountdown';
import CommandPalette from '../components/CommandPalette';
import DraftBanner from '../components/DraftBanner';
import StaleBanner from '../components/StaleBanner';
import { clearSessionDraft } from '../hooks/useSessionDraft';
import { stageCost, wordTotalCost, fmtUsd, type WordCostsDoc } from '../utils/wordCost';
import { writeLastWorked } from '../utils/lastWorked';
import './GameWorkspace.css';

interface ConflictState {
  serverVersion: string;
}

interface PendingSave {
  type: 'approve' | 'forceSave';
  selections?: Record<string, unknown>;
  fields?: Record<string, unknown>;
}

export interface GenStats {
  model: string;
  tokens: number;
  elapsedSec: number;
  promptFile?: string | null;
  promptVersion?: string | null;
}

// stage7/stage9 (social) retired 2026-07-05 — social content moves to the Social Engine.
const STAGE_ORDER: string[] = ['new', 'stage1', 'stage1b', 'stage2', 'stage3', 'stage4', 'stage5', 'stage6', 'stage8', 'stage10', 'stage11'];

function nextStage(current: string): string | null {
  const idx = STAGE_ORDER.indexOf(current);
  return idx >= 0 && idx < STAGE_ORDER.length - 1 ? STAGE_ORDER[idx + 1] : null;
}

// Anthropic: input_tokens + output_tokens. Ollama: prompt_tokens + completion_tokens.
function getTotalTokens(usage: Record<string, number> | undefined | null): number {
  if (!usage) return 0;
  const input = usage.input_tokens ?? usage.prompt_tokens ?? 0;
  const output = usage.output_tokens ?? usage.completion_tokens ?? 0;
  return input + output;
}

export default function GameWorkspace() {
  const { selectedWord, selectedWordId, updateWordField, setSelectedWordField, selectWord, fetchWords, dirtyFields, clearDirty, clearDirtyFields, wordVersion, setWordVersion } = useWordStore();
  const {
    autoGenerateNext,
    modelRegistry, taskConfig, tiers, modelRoutingLoaded, fetchModelRouting,
    modelPricing, fetchModelPricing,
  } = useConfigStore();
  const [viewingStage, setViewingStage] = useState<string | null>(null);
  const [generating, setGenerating] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldRegenerationFailed, setFieldRegenerationFailed] = useState<boolean>(false);
  const [lastGenStats, setLastGenStats] = useState<GenStats | null>(null);
  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const [bgGenerating, setBgGenerating] = useState<string | null>(null);
  const [bgReady, setBgReady] = useState<string | null>(null);
  const [bgError, setBgError] = useState<string | null>(null);
  // Unresolved-placeholder / weak-input warnings surfaced by the generate call
  // (audit fix 10: warn visibly, hard-block only pointless runs — server-side).
  const [inputWarnings, setInputWarnings] = useState<string[] | null>(null);
  const [saveDrops, setSaveDrops] = useState<{ fieldName: string; reason: string }[] | null>(null);
  // Stage 8 writes directly to the Related Words Content table and returns
  // `{ success, records }` with no parent-record fields. We cache the latest
  // run's records here so RelatedStage can render the success state (3 names
  // + created/updated badges + Airtable link). Cleared on word switch.
  const [stage8Records, setStage8Records] = useState<Array<{ id: string; word: string; action: string }> | null>(null);
  // Per-word failures from a partial stage-8 run (2026-07-05): the worker now
  // NAMES which related word failed instead of throwing a generic 500.
  const [stage8Failures, setStage8Failures] = useState<Array<{ word: string; phase: string; message: string }> | null>(null);
  const [stage4Evidence, setStage4Evidence] = useState<Stage4EvidenceRecord | null>(null);
  // Per-word/per-stage token-spend ledger (GET /api/words/:id/costs) — priced
  // client-side with the registry pricing map. See utils/wordCost.ts.
  const [wordCosts, setWordCosts] = useState<WordCostsDoc | null>(null);
  // Per-run model overrides, keyed `${wordId}:${stageId}` — session-only, per
  // word. Never touches the standing task → tier assignment in Settings.
  const [modelOverrides, setModelOverrides] = useState<Record<string, string>>({});
  const approvePayloadRef = useRef<(() => Record<string, unknown> | null) | null>(null);
  const rateLimitRef = useRef<RateLimitCountdownHandle | null>(null);
  const pendingSaveRef = useRef<PendingSave | null>(null);
  const bgPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // H7 fix: the poll's 800ms kickoff timeout must be clearable on word switch.
  const bgKickoffRef = useRef<number | null>(null);

  const { recordOriginals, captureEdit, captureGold, resetOriginals } = useLearningCapture(selectedWord, viewingStage || selectedWord?._stage || '');
  const { staleStages, fetchStale, markDownstreamStale, clearStaleStage } = useStaleStore();
  const { fetchPrefs, fetchEditRates, recordApproval, loaded: qaLoaded } = useQuickApproveStore();

  useEffect(() => {
    resetOriginals();
    fetchStale(selectedWordId);
    // Clear background generation state when switching words
    setBgGenerating(null);
    setBgReady(null);
    setBgError(null);
    setLastGenStats(null);
    setError(null);
    setFieldRegenerationFailed(false);
    setStage8Records(null);
    setStage8Failures(null);
    setStage4Evidence(null);
    // C1 fix (audit 2026-07-05): a word switch must not inherit the previous
    // word's viewed stage (or any stage component state — see renderStage keys).
    setViewingStage(null);
    if (bgPollRef.current) { clearInterval(bgPollRef.current); bgPollRef.current = null; }
    if (bgKickoffRef.current) { window.clearTimeout(bgKickoffRef.current); bgKickoffRef.current = null; }
  }, [selectedWordId, resetOriginals, fetchStale]);

  // Clean up background polling on unmount
  useEffect(() => {
    return () => {
      if (bgPollRef.current) clearInterval(bgPollRef.current);
      if (bgKickoffRef.current) window.clearTimeout(bgKickoffRef.current);
    };
  }, []);

  useEffect(() => {
    if (!qaLoaded) {
      fetchPrefs();
      fetchEditRates();
    }
  }, [qaLoaded, fetchPrefs, fetchEditRates]);

  // Model routing + pricing feed the stage meta bar (model selector + costs).
  useEffect(() => {
    if (!modelRoutingLoaded) fetchModelRouting();
    fetchModelPricing();
  }, [modelRoutingLoaded, fetchModelRouting, fetchModelPricing]);

  const fetchWordCosts = useCallback(async (wordId: string | null) => {
    if (!wordId) { setWordCosts(null); return; }
    try {
      const doc = await api.get(`/api/words/${wordId}/costs`) as WordCostsDoc;
      setWordCosts(doc && doc.stages ? doc : { stages: {} });
    } catch {
      setWordCosts(null); // cost display is advisory — never block the workspace
    }
  }, []);

  useEffect(() => {
    fetchWordCosts(selectedWordId);
  }, [selectedWordId, fetchWordCosts]);

  const currentStage = viewingStage || selectedWord?._stage || 'new';
  const currentTaskName: string | undefined = STAGE_TASK_MAP[currentStage] || undefined;
  const stageConfig = STAGE_CONFIG[currentStage];

  useEffect(() => {
    let cancelled = false;
    if (!selectedWordId) {
      setStage4Evidence(null);
      return;
    }
    void api.get(`/api/words/${selectedWordId}/fact-evidence`)
      .then((data) => {
        if (!cancelled) {
          setStage4Evidence((data as { evidence?: Stage4EvidenceRecord | null }).evidence || null);
        }
      })
      .catch(() => {
        if (!cancelled) setStage4Evidence(null);
      });
    return () => { cancelled = true; };
  }, [selectedWordId]);

  // "Resume where I left off": record the word + on-screen stage whenever
  // either changes (persisted to localStorage; read by the binder chip), and
  // consume a pending resume stage once the resumed word has loaded.
  const pendingResumeStage = useUiStore((s) => s.pendingResumeStage);
  const setPendingResumeStage = useUiStore((s) => s.setPendingResumeStage);
  useEffect(() => {
    if (!selectedWord?.id || !selectedWord.word) return;
    writeLastWorked({
      wordId: selectedWord.id,
      word: String(selectedWord.word),
      stage: currentStage,
      ts: new Date().toISOString(),
    });
  }, [selectedWord?.id, selectedWord?.word, currentStage]);
  useEffect(() => {
    if (!pendingResumeStage || !selectedWord?.id) return;
    if (STAGE_CONFIG[pendingResumeStage]) {
      setViewingStage(pendingResumeStage);
    }
    setPendingResumeStage(null);
  }, [pendingResumeStage, selectedWord?.id, setPendingResumeStage]);

  // Approve auto-advance: when the server is background-generating the stage
  // the user is currently viewing, present it exactly like a foreground
  // generate — spinner bar + disabled Generate/Regenerate buttons — so
  // approval flows straight into the next stage's content with no redundant
  // "Generate" click and no accidental duplicate run.
  const bgGeneratingHere = bgGenerating !== null && bgGenerating === currentStage;
  const effectiveGenerating = generating || bgGeneratingHere;

  // Per-run model override for the viewed stage (empty string = task default).
  const overrideKey = selectedWord ? `${selectedWord.id}:${currentStage}` : '';
  const currentOverride = (overrideKey && modelOverrides[overrideKey]) || '';

  // What the stage's task resolves to by default (Settings routing), for the
  // "Default — X" option label.
  const defaultModelLabel = (() => {
    const target = currentTaskName ? taskConfig[currentTaskName] : undefined;
    if (!target || target === 'inherits_from_stage') return 'task default';
    const tier = tiers.find((t) => t.tier === target);
    if (tier) return tier.displayName;
    const reg = modelRegistry.find((m) => m.id === target);
    return reg?.displayName || String(target);
  })();

  const getCompletedStages = useCallback((): Set<string> => {
    if (!selectedWord) return new Set<string>();
    const completed = new Set<string>();
    const stageIdx = STAGE_ORDER.indexOf(selectedWord._stage);
    for (let i = 0; i <= stageIdx; i++) {
      completed.add(STAGE_ORDER[i]);
    }
    return completed;
  }, [selectedWord]);

  // Generate handler
  const handleGenerate = useCallback(async () => {
    if (!selectedWord || !stageConfig?.generateEndpoint) return;
    setGenerating(true);
    setError(null);
    setFieldRegenerationFailed(false);
    setInputWarnings(null);
    const t0 = Date.now();
    try {
      const result = await api.post(stageConfig.generateEndpoint, {
        wordId: selectedWord.id,
        word: selectedWord,
        modelOverride: currentOverride || undefined,
      }) as {
        fields?: Record<string, unknown>;
        model?: string;
        usage?: Record<string, number>;
        success?: boolean;
        stageId?: string;
        records?: Array<{ id: string; word: string; action: string }>;
        partial?: boolean;
        failures?: Array<{ word: string; phase: string; message: string }>;
        parentVersion?: string | null;
        prompt?: { system?: { file?: string; version?: string } | null } | null;
        inputWarnings?: string[];
        factEvidence?: Stage4EvidenceRecord;
      };
      const elapsedSec = Math.max(1, Math.round((Date.now() - t0) / 1000));

      if (Array.isArray(result.inputWarnings) && result.inputWarnings.length > 0) {
        setInputWarnings(result.inputWarnings);
      }
      if (currentStage === 'stage4') {
        setStage4Evidence(result.factEvidence || null);
      }

      // Stage 8 writes the 3 related-word records directly to the Related
      // Words Content table and flips related_words_generated on the parent
      // server-side. It deliberately returns no `fields`, so the autosave
      // PUT below is skipped by the existing `if (result.fields)` guard.
      // Mirror the parent gate locally and cache the records for the UI.
      // The worker also echoes the parent's fresh version hash so the
      // client can refresh its optimistic-concurrency pointer — without
      // this, a follow-up Approve 409s against the post-flip server hash.
      if (result.success && Array.isArray(result.records) && currentStage === 'stage8') {
        setStage8Records(result.records);
        setStage8Failures(Array.isArray(result.failures) && result.failures.length > 0 ? result.failures : null);
        if (!result.partial) {
          // Full success: server flipped the gate — mirror locally without
          // marking dirty. On PARTIAL success the gate stays down until every
          // word succeeds, so nothing to mirror.
          setSelectedWordField('related_words_generated', true);
          if (result.parentVersion) setWordVersion(result.parentVersion);
          await clearStaleStage(selectedWord.id, currentStage);
        }
      }

      if (result.fields) {
        recordOriginals(result.fields as Record<string, string>);
        // H10 fix (audit 2026-07-05): PERSIST FIRST, mirror after. The old
        // order mirrored fields as clean before the PUT — a failed PUT left
        // content on screen that looked saved but wasn't (no dirty flag, no
        // draft, retry = pay for another generation).
        console.log('[autosave] Saving', Object.keys(result.fields).length, 'fields for', selectedWord.id);
        try {
          // H10 fix (2026-07-05): send `_version` so a cross-tab / background
          // change is detected (409) instead of silently overwritten. Safe:
          // version.js excludes generation-output fields from the hash and the
          // generate call hasn't written to Airtable yet, so the pre-generate
          // hash still matches the server record — no false 409 on a clean save.
          const putRes = await api.put(`/api/words/${selectedWord.id}`, { fields: result.fields, _version: wordVersion || undefined }) as { word?: { _version?: string } };
          if (putRes.word?._version) setWordVersion(putRes.word._version);
          console.log('[autosave] Success');
          // Server-confirmed — mirror without dirty-marking.
          for (const [field, value] of Object.entries(result.fields)) {
            setSelectedWordField(field, value);
          }
          clearDirtyFields(Object.keys(result.fields));
          await clearStaleStage(selectedWord.id, currentStage);
        } catch (putErr) {
          // Keep the paid output recoverable: mirror it as DIRTY so Cmd+S /
          // approve / the session draft can persist it.
          for (const [field, value] of Object.entries(result.fields)) {
            updateWordField(field, value);
          }
          if (putErr instanceof ApiError && putErr.status === 409 && (putErr.data as Record<string, unknown>)?.conflict) {
            // H10: route the version conflict through the existing conflict UI.
            // The dirty-mirrored fields become the overwrite payload if chosen.
            pendingSaveRef.current = { type: 'forceSave', fields: result.fields };
            setConflict({ serverVersion: (putErr.data as Record<string, unknown>).serverVersion as string });
          } else {
            setError(`Generated content is on screen but did NOT save to Airtable (${(putErr as Error).message}). It's held as unsaved edits — press Cmd+S or Approve to persist without regenerating.`);
          }
        }
      }
      if (result.model) {
        setLastGenStats({
          model: result.model,
          tokens: getTotalTokens(result.usage),
          elapsedSec,
          promptFile: result.prompt?.system?.file ?? null,
          promptVersion: result.prompt?.system?.version ?? null,
        });
      }
      fetchWordCosts(selectedWord.id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429 && rateLimitRef.current) {
        rateLimitRef.current.start(err.retryAfter ?? 30);
      } else {
        setError((err as Error).message);
      }
    } finally {
      setGenerating(false);
    }
  }, [selectedWord, stageConfig, setSelectedWordField, updateWordField, recordOriginals, clearDirtyFields, currentStage, clearStaleStage, currentOverride, fetchWordCosts]);

  // Compare handler — same generation call, returns fields without updating store
  const handleCompare = useCallback(async (): Promise<{ fields: Record<string, unknown>; model: string | null } | null> => {
    if (!selectedWord || !stageConfig?.generateEndpoint) return null;
    setGenerating(true);
    setError(null);
    setFieldRegenerationFailed(false);
    try {
      const result = await api.post(stageConfig.generateEndpoint, {
        wordId: selectedWord.id,
        word: selectedWord,
        modelOverride: currentOverride || undefined,
        preview: true,
      }) as { fields?: Record<string, unknown>; model?: string };
      fetchWordCosts(selectedWord.id);
      return { fields: result.fields || {}, model: result.model || null };
    } catch (err) {
      if (err instanceof ApiError && err.status === 429 && rateLimitRef.current) {
        rateLimitRef.current.start(err.retryAfter ?? 30);
      } else {
        setError((err as Error).message);
      }
      return null;
    } finally {
      setGenerating(false);
    }
  }, [selectedWord, stageConfig, currentOverride, fetchWordCosts]);

  // Regenerate a single field — same endpoint, but asks the LLM to produce only one field
  const handleRegenerateField = useCallback(async (fieldName: string, instructions?: string): Promise<{ value: string; model: string | null; tokens: number; elapsedSec: number } | null> => {
    if (!selectedWord || !stageConfig?.generateEndpoint) return null;
    setError(null);
    setFieldRegenerationFailed(false);
    const t0 = Date.now();
    try {
      const result = await api.post(stageConfig.generateEndpoint, {
        wordId: selectedWord.id,
        word: selectedWord,
        regenerateField: fieldName,
        regenInstructions: instructions || undefined,
        modelOverride: currentOverride || undefined,
      }) as { fields?: Record<string, unknown>; model?: string; usage?: Record<string, number>; factEvidence?: Stage4EvidenceRecord };
      const elapsedSec = Math.max(1, Math.round((Date.now() - t0) / 1000));
      fetchWordCosts(selectedWord.id);
      if (currentStage === 'stage4' && result.factEvidence) {
        setStage4Evidence(result.factEvidence);
      }
      const value = result.fields?.[fieldName];
      if (value === undefined) return null;
      return {
        value: String(value),
        model: result.model || null,
        tokens: getTotalTokens(result.usage),
        elapsedSec,
      };
    } catch (err) {
      if (err instanceof ApiError && err.status === 429 && rateLimitRef.current) {
        rateLimitRef.current.start(err.retryAfter ?? 30);
      } else {
        setFieldRegenerationFailed(true);
        setError((err as Error).message);
      }
      return null;
    }
  }, [selectedWord, stageConfig, currentOverride, currentStage, fetchWordCosts]);

  // Approve handler — saves selections to Airtable and advances
  const doSave = useCallback(async (selections: Record<string, unknown>, forceVersion?: string | null, approvedStage?: string): Promise<Record<string, unknown>> => {
    const version = forceVersion !== undefined ? forceVersion : wordVersion;
    const result = await api.put(`/api/words/${selectedWord!.id}`, {
      fields: selections,
      _version: version || undefined,
      _approvedStage: approvedStage || undefined,
    }) as { word?: { _version?: string }; droppedFields?: { fieldName: string; reason: string }[] };
    if (result.word?._version) {
      setWordVersion(result.word._version);
    }
    if (result.droppedFields && result.droppedFields.length > 0) {
      setSaveDrops(result.droppedFields);
    }
    return result as Record<string, unknown>;
  }, [selectedWord, wordVersion, setWordVersion]);

  const startBgPoll = useCallback((wordId: string) => {
    if (bgPollRef.current) clearInterval(bgPollRef.current);
    const started = Date.now();

    const tick = async () => {
      // H7 fix: if the user switched words, this poll must go quiet — it must
      // never yank the selection back or write state for the wrong word.
      if (useWordStore.getState().selectedWordId !== wordId) {
        if (bgPollRef.current) clearInterval(bgPollRef.current);
        bgPollRef.current = null;
        return;
      }
      if (Date.now() - started > 300_000) {
        if (bgPollRef.current) clearInterval(bgPollRef.current);
        bgPollRef.current = null;
        setBgGenerating(null);
        setBgError('Background generation timed out.');
        return;
      }
      try {
        const status = await api.get(`/api/words/${wordId}/bg-status`) as { status: string; stage?: string; error?: string };
        if (useWordStore.getState().selectedWordId !== wordId) {
          if (bgPollRef.current) clearInterval(bgPollRef.current);
          bgPollRef.current = null;
          return;
        }
        if (status.status === 'complete') {
          if (bgPollRef.current) clearInterval(bgPollRef.current);
          bgPollRef.current = null;
          setBgGenerating(null);
          setBgReady(status.stage || null);
          // H7 fix: reloading the word must not wipe values edited while the
          // background run was in flight — snapshot dirty values and re-apply
          // them (still dirty) after the refresh.
          const store = useWordStore.getState();
          const dirtySnapshot: Record<string, unknown> = {};
          if (store.selectedWord && store.dirtyFields) {
            for (const f of store.dirtyFields) {
              dirtySnapshot[f] = (store.selectedWord as unknown as Record<string, unknown>)[f];
            }
          }
          await selectWord(wordId);
          for (const [f, v] of Object.entries(dirtySnapshot)) {
            useWordStore.getState().updateWordField(f, v);
          }
          fetchWordCosts(wordId);
          api.delete(`/api/words/${wordId}/bg-status`).catch(() => {});
        } else if (status.status === 'failed') {
          if (bgPollRef.current) clearInterval(bgPollRef.current);
          bgPollRef.current = null;
          setBgGenerating(null);
          setBgError(status.error || 'Background generation failed.');
        }
      } catch {
        // ignore poll errors, retry next interval
      }
    };

    bgPollRef.current = setInterval(tick, 5000);
    // First check ~right away: already-populated stages resolve as "skipped"
    // server-side almost instantly, so the user shouldn't stare at a spinner
    // for a full poll interval. Small delay gives ctx.waitUntil a beat to
    // write the initial status doc. Tracked so a word switch can cancel it (H7).
    bgKickoffRef.current = window.setTimeout(tick, 800);
  }, [selectWord, fetchWordCosts]);

  const handleApprove = useCallback(async (selections: Record<string, unknown>): Promise<void> => {
    if (!selectedWord) return;
    setSaving(true);
    setError(null);
    setConflict(null);
    try {
      const hadEdits = dirtyFields && dirtyFields.size > 0;

      // C2 fix (audit 2026-07-05): approve used to PUT only the selection
      // indexes and then wipe dirty tracking — inline edits were silently
      // dropped server-side while the UI showed them as saved. Merge every
      // dirty field's current value into the approve payload (selections win
      // on conflict). Underscore-prefixed pseudo-fields never go to Airtable.
      const editPayload: Record<string, unknown> = {};
      if (hadEdits) {
        for (const f of dirtyFields) {
          if (f.startsWith('_')) continue;
          editPayload[f] = (selectedWord as unknown as Record<string, unknown>)[f];
        }
      }
      const payload = { ...editPayload, ...selections };

      await doSave(payload, undefined, currentStage);
      // Server-confirmed selections — mirror without dirty-marking.
      for (const [field, value] of Object.entries(selections)) {
        setSelectedWordField(field, value);
      }
      clearDirty();
      clearSessionDraft();

      const completedSet = getCompletedStages();
      if (hadEdits && completedSet.has(currentStage)) {
        await markDownstreamStale(selectedWord.id, currentStage);
      }
      if (staleStages.includes(currentStage)) {
        await clearStaleStage(selectedWord.id, currentStage);
      }

      if (currentTaskName) {
        recordApproval(currentTaskName, hadEdits);
      }

      // Approved-gold: capture the fields approved AS-IS (unedited) as positive
      // exemplars for the prompt-improvement loop. captureGold only posts fields
      // whose current value still equals the recorded AI original.
      captureGold(selectedWord as unknown as Record<string, unknown>);

      const next = nextStage(currentStage);
      if (next) {
        setViewingStage(next);
      }
      fetchWords();

      // Fire-and-forget: when open revisions exceed threshold, auto-extract patterns.
      // Doesn't block the approve flow — failure is logged but swallowed.
      void (async () => {
        try {
          const openData = await api.get('/api/learning/revisions?status=open') as { total?: number };
          const count = openData?.total ?? 0;
          if (count > 10) {
            console.log(`[learning] ${count} open revisions — triggering auto-extract`);
            const result = await api.post('/api/learning/extract', {});
            console.log('[learning] Extract result:', result);
            window.dispatchEvent(new CustomEvent('learning:proposals-updated'));
          }
        } catch (err) {
          console.warn('[learning] Auto-extract check failed:', (err as Error).message);
        }
      })();

      // Start background generation polling if enabled
      if (autoGenerateNext && currentStage) {
        const NEXT_STAGE: Record<string, string> = {
          new: 'stage1', stage1: 'stage1b', stage1b: 'stage2',
          stage2: 'stage3', stage3: 'stage4', stage4: 'stage5',
          stage5: 'stage6', stage6: 'stage8',
        };
        const nextBgStage = NEXT_STAGE[currentStage];
        if (nextBgStage) {
          setBgGenerating(nextBgStage);
          setBgReady(null);
          setBgError(null);
          startBgPoll(selectedWord.id);
        }
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && (err.data as Record<string, unknown>)?.conflict) {
        pendingSaveRef.current = { type: 'approve', selections };
        setConflict({ serverVersion: (err.data as Record<string, unknown>).serverVersion as string });
      } else {
        setError((err as Error).message);
      }
    } finally {
      setSaving(false);
    }
  }, [selectedWord, currentStage, currentTaskName, setSelectedWordField, fetchWords, clearDirty, dirtyFields, recordApproval, captureGold, getCompletedStages, markDownstreamStale, clearStaleStage, staleStages, doSave, autoGenerateNext, startBgPoll]);

  // Force save (Cmd+S) — save without advancing
  const handleForceSave = useCallback(async () => {
    // L19 fix (2026-07-05): bail if a save is already in flight (Cmd+S reflex).
    if (!selectedWord || saving || !dirtyFields || dirtyFields.size === 0) return;
    setSaving(true);
    setError(null);
    setConflict(null);
    // Snapshot exactly the fields being saved. L19: clear only THESE afterward —
    // a blanket clearDirty() would drop fields the user edits mid-save.
    const savedFields = [...dirtyFields];
    try {
      const fields: Record<string, unknown> = {};
      for (const field of savedFields) {
        fields[field] = selectedWord[field];
      }
      await doSave(fields);
      clearDirtyFields(savedFields);
      // Only wipe the recovery draft if nothing new was dirtied during the save.
      if (useWordStore.getState().dirtyFields.size === 0) clearSessionDraft();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && (err.data as Record<string, unknown>)?.conflict) {
        pendingSaveRef.current = { type: 'forceSave', fields: Object.fromEntries([...dirtyFields].map((f) => [f, selectedWord[f]])) };
        setConflict({ serverVersion: (err.data as Record<string, unknown>).serverVersion as string });
      } else {
        setError((err as Error).message);
      }
    } finally {
      setSaving(false);
    }
  }, [selectedWord, saving, dirtyFields, clearDirtyFields, doSave]);

  const handleConflictReload = useCallback(async () => {
    setConflict(null);
    pendingSaveRef.current = null;
    if (selectedWord) {
      await selectWord(selectedWord.id);
      clearDirty();
    }
  }, [selectedWord, selectWord, clearDirty]);

  const handleConflictOverwrite = useCallback(async () => {
    const pending = pendingSaveRef.current;
    if (!pending || !selectedWord) return;
    setConflict(null);
    pendingSaveRef.current = null;
    setSaving(true);
    try {
      if (pending.type === 'approve' && pending.selections) {
        // L2 fix (2026-07-05): pass _approvedStage so an overwrite-approve still
        // triggers the server's next-stage generation, matching a normal approve
        // (it was previously dropped, so downstream content never kicked off).
        await doSave(pending.selections, null, currentStage);
        // Server-confirmed selections (post-conflict-overwrite) — no dirty.
        for (const [field, value] of Object.entries(pending.selections)) {
          setSelectedWordField(field, value);
        }
        clearDirty();
        clearSessionDraft();
        const next = nextStage(currentStage);
        if (next) setViewingStage(next);
        fetchWords();
      } else if (pending.fields) {
        await doSave(pending.fields, null);
        clearDirty();
        clearSessionDraft();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [selectedWord, currentStage, setSelectedWordField, fetchWords, clearDirty, doSave]);

  // Edit handler
  const handleEdit = useCallback((field: string, newValue: string) => {
    captureEdit(field, newValue);
    updateWordField(field, newValue);
  }, [updateWordField, captureEdit]);

  const handleStageNavigate = useCallback((stageId: string) => {
    setError(null);
    setFieldRegenerationFailed(false);
    setViewingStage(stageId);
  }, []);

  const handleRegenerateAllStale = useCallback(async () => {
    if (!selectedWord || generating) return;
    const toRegen = staleStages.filter((s) => STAGE_CONFIG[s]?.generateEndpoint);
    if (toRegen.length === 0) return;
    setGenerating(true);
    setError(null);
    // H10 fix (2026-07-05): thread the freshest `_version` through the loop —
    // each stage's PUT can advance the server hash, so re-read it per iteration
    // to avoid a false 409 on the next stage.
    let ver = wordVersion;
    try {
      for (const stage of toRegen) {
        const endpoint = STAGE_CONFIG[stage]?.generateEndpoint;
        if (!endpoint) continue;
        const result = await api.post(endpoint, {
          wordId: selectedWord.id,
          word: selectedWord,
        }) as { fields?: Record<string, unknown> };
        if (result.fields) {
          // Persist FIRST, mirror on success — see handleGenerate rationale.
          try {
            const putRes = await api.put(`/api/words/${selectedWord.id}`, { fields: result.fields, _version: ver || undefined }) as { word?: { _version?: string } };
            if (putRes.word?._version) { ver = putRes.word._version; setWordVersion(ver); }
            for (const [field, value] of Object.entries(result.fields)) {
              setSelectedWordField(field, value);
            }
            clearDirtyFields(Object.keys(result.fields));
          } catch (putErr) {
            // Keep the paid output recoverable as dirty edits.
            for (const [field, value] of Object.entries(result.fields)) {
              updateWordField(field, value);
            }
            if (putErr instanceof ApiError && putErr.status === 409 && (putErr.data as Record<string, unknown>)?.conflict) {
              pendingSaveRef.current = { type: 'forceSave', fields: result.fields };
              setConflict({ serverVersion: (putErr.data as Record<string, unknown>).serverVersion as string });
              return; // stop the loop; user resolves the conflict
            }
            throw putErr;
          }
        }
        await clearStaleStage(selectedWord.id, stage);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setGenerating(false);
    }
  }, [selectedWord, generating, staleStages, wordVersion, setWordVersion, setSelectedWordField, updateWordField, clearDirtyFields, clearStaleStage]);

  // Register approve payload from current stage
  const registerApprovePayload = useCallback((fn: (() => Record<string, unknown> | null) | null) => {
    approvePayloadRef.current = fn;
  }, []);

  // Sync pipeline store for keyboard shortcuts
  useEffect(() => {
    usePipelineStore.getState().register({
      _generate: stageConfig?.generateEndpoint ? handleGenerate : null,
      _approve: handleApprove,
      _forceSave: handleForceSave,
      _navigateStage: handleStageNavigate,
      _getApprovePayload: () => (approvePayloadRef.current ? approvePayloadRef.current() : null),
      generating,
      saving,
      hasWord: !!selectedWord,
      currentStage,
      completedStages: getCompletedStages(),
    });
  }, [handleGenerate, handleApprove, handleForceSave, handleStageNavigate, generating, saving, selectedWord, currentStage, stageConfig, getCompletedStages]);

  useEffect(() => {
    return () => usePipelineStore.getState().clear();
  }, []);

  const newWordMode = useUiStore((s) => s.newWordMode);

  if (newWordMode) {
    return (
      <div className="game-workspace">
        <CommandPalette />
        <div className="stage-content">
          <WordEntryForm />
        </div>
      </div>
    );
  }

  if (!selectedWordId) {
    return (
      <div className="game-workspace">
        <CommandPalette />
        <div className="workspace-content">
          <div className="workspace-empty">
            <p className="workspace-empty-title">Content</p>
            <p className="workspace-empty-hint">Select a word from the Binder, or click + New Word in the sidebar.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedWord) {
    return (
      <div className="game-workspace">
        <CommandPalette />
        <div className="workspace-loading">Loading word...</div>
      </div>
    );
  }

  return (
    <div className="game-workspace">
      <CommandPalette />
      <DraftBanner />

      <div className="word-header">
        <div className="word-header-main">
          <h1 className="word-header-word">{selectedWord.word}</h1>
          <div className="word-header-meta">
            {selectedWord.part_of_speech && (
              <span className="word-pos">{selectedWord.part_of_speech}</span>
            )}
            {selectedWord.phonetics && (
              <span className="word-phonetics">{selectedWord.phonetics}</span>
            )}
            {selectedWord.difficulty && (
              <span className={`word-diff diff-${selectedWord.difficulty?.toLowerCase()}`}>
                {selectedWord.difficulty}
              </span>
            )}
            {(() => {
              const total = wordTotalCost(wordCosts, modelRegistry, modelPricing);
              return total ? (
                <span
                  className="word-cost-total"
                  title={`Estimated generation spend for this word across all stages (${total.calls} call${total.calls !== 1 ? 's' : ''}${total.partial ? '; some calls had no known price and are excluded' : ''})`}
                >
                  {total.partial ? '≥' : ''}{fmtUsd(total.usd)} total
                </span>
              ) : null;
            })()}
          </div>
        </div>
        {selectedWord.audio_url && (
          <button
            className="word-audio-btn"
            onClick={() => new Audio(selectedWord.audio_url).play()}
            title="Play pronunciation"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <polygon points="5,3 17,10 5,17" fill="currentColor"/>
            </svg>
          </button>
        )}
      </div>

      <StageNav
        currentStage={currentStage}
        completedStages={getCompletedStages()}
        onNavigate={handleStageNavigate}
        staleStages={new Set(staleStages)}
        bgGenerating={bgGenerating}
        bgReady={bgReady}
      />

      {error && (
        <div className="stage-error">
          <span>{error}</span>
          <div className="stage-error-actions">
            {stageConfig?.generateEndpoint && !fieldRegenerationFailed && (
              <button className="stage-error-retry" onClick={() => { setError(null); handleGenerate(); }}>Retry</button>
            )}
            <button className="stage-error-dismiss" onClick={() => { setError(null); setFieldRegenerationFailed(false); }}>Dismiss</button>
          </div>
        </div>
      )}

      {conflict && (
        <div className="stage-conflict">
          <div className="stage-conflict-text">
            <strong className="stage-conflict-title">Word changed since you loaded it</strong>
            <span className="stage-conflict-body">
              This word's content in Airtable is different from what you started editing. This usually means another tab updated it, or a generate completed in the background. To avoid losing work, choose how to handle it.
            </span>
          </div>
          <div className="stage-conflict-actions">
            <button className="stage-conflict-reload" onClick={handleConflictReload}>
              Reload (drops your unsaved edits)
            </button>
            <button className="stage-conflict-overwrite" onClick={handleConflictOverwrite}>
              Save my version (overwrites Airtable)
            </button>
          </div>
        </div>
      )}

      <RateLimitCountdown
        ref={rateLimitRef}
        onRetry={handleGenerate}
        onSettings={() => useUiStore.getState().setPage('settings')}
      />

      <StaleBanner
        wordId={selectedWord.id}
        currentStage={currentStage}
        onGenerate={handleGenerate}
        onRegenerateAllStale={handleRegenerateAllStale}
        generating={effectiveGenerating}
      />

      {bgError && (
        <div className="bg-error-banner">
          {bgError}
          {stageConfig?.generateEndpoint && (
            <button onClick={() => { setBgError(null); handleGenerate(); }}>Retry</button>
          )}
          <button onClick={() => setBgError(null)}>Dismiss</button>
        </div>
      )}
      {inputWarnings && inputWarnings.length > 0 && (
        <div className="bg-error-banner">
          <span>
            Generation ran with incomplete inputs: {inputWarnings.join('; ')}. Review the output carefully.
          </span>
          <button onClick={() => setInputWarnings(null)}>Dismiss</button>
        </div>
      )}
      {saveDrops && saveDrops.length > 0 && (
        <div className="bg-error-banner">
          <span>
            Some fields did not save: {saveDrops.map((d) => d.fieldName).join(', ')}. The stage may not have fully persisted.
          </span>
          <button onClick={() => setSaveDrops(null)}>Dismiss</button>
        </div>
      )}

      {effectiveGenerating && (
        <div className="stage-loading-bar" role="status" aria-live="polite">
          <div className="stage-loading-spinner" aria-hidden="true" />
          <span>
            {bgGeneratingHere && !generating
              ? `Auto-generating ${stageConfig?.label || 'content'} (started by your approval)...`
              : `Generating ${stageConfig?.label || 'content'}...`}
          </span>
        </div>
      )}

      {/* Stage meta bar: which model this stage runs on (with a per-run
          override) + what this stage has cost so far on this word. */}
      {currentTaskName && (
        <div className="stage-meta-bar">
          <label className="stage-model-select">
            <span className="stage-meta-label">Model</span>
            <select
              value={currentOverride}
              disabled={effectiveGenerating}
              onChange={(e) => {
                const v = e.target.value;
                setModelOverrides((prev) => {
                  const next = { ...prev };
                  if (v) next[overrideKey] = v;
                  else delete next[overrideKey];
                  return next;
                });
              }}
              title="Model for this stage's next generation on THIS word only. The standing Settings assignment is unchanged."
            >
              <option value="">Default — {defaultModelLabel}</option>
              {modelRegistry.filter((m) => m.id).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.provider})
                </option>
              ))}
            </select>
            {currentOverride && <span className="stage-override-badge" title="Per-word override active — applies to this word's next generation only">override</span>}
          </label>
          {(() => {
            const entry = wordCosts?.stages?.[currentStage];
            const c = stageCost(entry, modelRegistry, modelPricing);
            if (!c) return <span className="stage-meta-cost muted">No spend recorded for this stage yet</span>;
            return (
              <span className="stage-meta-cost" title={`Estimated spend for ${stageConfig?.label || currentStage} on this word (${c.calls} call${c.calls !== 1 ? 's' : ''}${c.partial ? '; some calls had no known price and are excluded' : ''})`}>
                This stage: {c.partial ? '≥' : ''}{fmtUsd(c.usd)} · {c.calls} call{c.calls !== 1 ? 's' : ''}
              </span>
            );
          })()}
        </div>
      )}

      <div className="stage-content">
        {renderStage()}
      </div>
    </div>
  );

  function renderStage(): JSX.Element {
    if (!stageConfig) {
      return <div className="workspace-empty"><p>Unknown stage: {currentStage}</p></div>;
    }

    // selectedWord is guaranteed non-null here (early return above)
    const word = selectedWord!;

    switch (stageConfig.mode) {
      case 'entry':
        if (word.id && word.word) {
          return (
            <div className="stage-view">
              <div className="stage-generate-area">
                <button
                  className="generate-btn"
                  disabled={generating}
                  title="Generate Definition & Core Identity (\u2318G)"
                  onClick={async () => {
                    setGenerating(true);
                    setError(null);
                    try {
                      const result = await api.post('/api/generate/stage1', {
                        wordId: word.id,
                        word,
                      }) as { fields?: Record<string, unknown>; model?: string };
                      if (result.fields) {
                        recordOriginals(result.fields as Record<string, string>);
                        console.log('[autosave] Saving', Object.keys(result.fields).length, 'fields for', word.id);
                        // H10 fix (2026-07-05): persist FIRST, mirror after;
                        // send `_version`; on 409 route to the conflict UI.
                        try {
                          const putRes = await api.put(`/api/words/${word.id}`, { fields: result.fields, _version: wordVersion || undefined }) as { word?: { _version?: string } };
                          if (putRes.word?._version) setWordVersion(putRes.word._version);
                          console.log('[autosave] Success');
                          for (const [field, value] of Object.entries(result.fields)) {
                            setSelectedWordField(field, value);
                          }
                          clearDirtyFields(Object.keys(result.fields));
                        } catch (putErr) {
                          // Keep the paid output recoverable as dirty edits.
                          for (const [field, value] of Object.entries(result.fields)) {
                            updateWordField(field, value);
                          }
                          if (putErr instanceof ApiError && putErr.status === 409 && (putErr.data as Record<string, unknown>)?.conflict) {
                            pendingSaveRef.current = { type: 'forceSave', fields: result.fields };
                            setConflict({ serverVersion: (putErr.data as Record<string, unknown>).serverVersion as string });
                          } else {
                            throw putErr;
                          }
                        }
                      }
                      setViewingStage('stage1');
                    } catch (err) {
                      if (err instanceof ApiError && err.status === 429 && rateLimitRef.current) {
                        rateLimitRef.current.start(err.retryAfter ?? 30);
                      } else {
                        setError((err as Error).message);
                      }
                    } finally {
                      setGenerating(false);
                    }
                  }}
                >
                  {generating ? 'Generating...' : 'Generate Definition'}
                </button>
              </div>
            </div>
          );
        }
        return <WordEntryForm />;

      case 'review':
        return (
          <ReviewStage
            key={`${word.id}:${currentStage}`}
            word={word}
            config={stageConfig}
            taskName={currentTaskName}
            onGenerate={handleGenerate}
            onApprove={handleApprove}
            onEdit={handleEdit}
            onRegenerateField={handleRegenerateField}
            lastGenStats={lastGenStats}
            generating={effectiveGenerating}
            saving={saving}
            registerApprovePayload={registerApprovePayload}
          />
        );

      case 'pick':
        return (
          <PickStage
            key={`${word.id}:${currentStage}`}
            word={word}
            config={stageConfig}
            taskName={currentTaskName}
            onGenerate={handleGenerate}
            onCompare={currentStage === 'stage4' ? undefined : handleCompare}
            onApprove={handleApprove}
            onEdit={handleEdit}
            onRegenerateField={handleRegenerateField}
            lastModel={lastGenStats?.model || null}
            generating={effectiveGenerating}
            saving={saving}
            registerApprovePayload={registerApprovePayload}
            factEvidence={currentStage === 'stage4' ? stage4Evidence : null}
          />
        );

      case 'reframes':
        return (
          <ReframesStage
            key={`${word.id}:${currentStage}`}
            word={word}
            config={{ tiers: stageConfig.tiers! }}
            onGenerate={handleGenerate}
            onApprove={handleApprove}
            onEdit={handleEdit}
            onRegenerateField={handleRegenerateField}
            generating={effectiveGenerating}
            saving={saving}
            registerApprovePayload={registerApprovePayload}
          />
        );

      case 'audio':
        return (
          <AudioStage
            key={`${word.id}:${currentStage}`}
            word={word}
            onGenerate={handleGenerate}
            onApprove={handleApprove}
            generating={effectiveGenerating}
            saving={saving}
            registerApprovePayload={registerApprovePayload}
          />
        );

      case 'related':
        return (
          <RelatedStage
            key={`${word.id}:${currentStage}`}
            word={word}
            onGenerate={handleGenerate}
            onApprove={handleApprove}
            generating={effectiveGenerating}
            saving={saving}
            registerApprovePayload={registerApprovePayload}
            stage8Records={stage8Records}
            stage8Failures={stage8Failures}
            onClearFailures={() => setStage8Failures(null)}
          />
        );

      case 'final-review':
        return (
          <FinalReviewStage
            key={`${word.id}:${currentStage}`}
            word={word}
            onApprove={handleApprove}
            onNavigate={handleStageNavigate}
            saving={saving}
            registerApprovePayload={registerApprovePayload}
          />
        );

      case 'transfer':
        return (
          <TransferStage
            key={`${word.id}:${currentStage}`}
            word={word}
            onApprove={handleApprove}
            saving={saving}
            registerApprovePayload={registerApprovePayload}
          />
        );

      default:
        return <div className="workspace-empty"><p>Stage not implemented: {stageConfig.mode}</p></div>;
    }
  }
}
